if (!customElements.get('kibbu-personalizzato')) {
  customElements.define('kibbu-personalizzato', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.palette = JSON.parse(this.querySelector('[data-palette]').textContent);
      this.form = this.querySelector('[data-config]');
      this.files = new Map();
      this.objectUrls = new Map();
      this.uploaded = new Map();
      this.requestId = crypto.randomUUID();
      this.status = this.querySelector('[data-status]');
      this.checkout = this.querySelector('[data-checkout]');
      this.bundle = this.querySelector('[data-bundle]');
      this.bowlSelect = this.querySelector('[data-bowl]');
      this.sizeSelect = this.querySelector('[data-bowl-variant]');
      this.emailInput = this.querySelector('[data-email]');
      this.nameInput = this.querySelector('[data-name]');
      this.mobileQuery = window.matchMedia?.('(max-width: 749px)');
      this.profiles = this.querySelector('[data-profile-options]');
      this.coverDialog = this.querySelector('[data-cover-dialog]');
      this.photos = this.querySelector('.kibbu-personalizzato__photos');
      this.profileGrid = this.querySelector('.kibbu-personalizzato__profile-grid');
      this.onViewport = () => {
        if (!this.profiles) return;
        const mobile = this.mobileQuery.matches;
        if (this.photos && this.profileGrid) for (const role of ['left','right']) {
          const card = this.querySelector(`[data-photo="${role}"]`);
          if (mobile) this.profileGrid.append(card);
          else this.photos.insertBefore(card, this.profiles);
        }
        this.profiles.open = mobile ? !!this.mobileProfilesOpen : true;
      };
      this.onProfilesToggle = () => { if (this.mobileQuery?.matches) this.mobileProfilesOpen = this.profiles.open; };
      this.mobileQuery?.addEventListener('change', this.onViewport);
      this.profiles?.addEventListener('toggle', this.onProfilesToggle);
      if (this.mobileQuery) this.onViewport();
      this.onClick = (event) => {
        if (event.target === this.coverDialog) {
          const rect = this.coverDialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.coverDialog.close();
        }
        const button = event.target.closest('button');
        if (!button || this.busy) return;
        if (button.hasAttribute('data-open-cover') && this.coverDialog) {
          const cover = this.querySelector('.kibbu-personalizzato__hero--cover img');
          this.showImage(this.querySelector('[data-cover-image]'), cover.currentSrc || cover.src, cover.alt, 900);
          this.coverDialog.showModal();
        }
        if (button.hasAttribute('data-close-cover')) this.coverDialog?.close();
        if (button.hasAttribute('data-mobile-next')) this.mobileNext();
        if (button.hasAttribute('data-base')) { this.base = button.dataset.base; this.changed(); }
        if (button.hasAttribute('data-color')) { this.color = button.dataset.color; this.changed(); }
        if (button.hasAttribute('data-remove')) this.removeFile(button.dataset.remove);
        if (button.hasAttribute('data-retry')) this.load();
      };
      this.onChange = (event) => {
        if (this.busy) return;
        if (event.target.matches('[data-file]')) this.chooseFile(event.target);
        else if (event.target === this.bowlSelect) { this.bowlId = this.bowlSelect.value; this.changed(); }
        else if (event.target === this.sizeSelect) { this.bowlVariantId = this.sizeSelect.value; this.rememberSize(); this.changed(); }
        else if (event.target === this.bundle) this.changed();
        else if (event.target.matches('[data-consent]')) this.updateMobileFlow();
      };
      this.onInput = (event) => { if ((event.target === this.nameInput || event.target === this.emailInput) && !this.busy) { this.resetRequest(); this.updateMobileFlow(); } };
      this.onSubmit = (event) => this.submit(event);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);
      this.load();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.form.removeEventListener('submit', this.onSubmit);
      this.mobileQuery?.removeEventListener('change', this.onViewport);
      this.profiles?.removeEventListener('toggle', this.onProfilesToggle);
      if (this.coverDialog?.open) this.coverDialog.close();
      for (const url of this.objectUrls.values()) URL.revokeObjectURL(url);
      this.initialized = false;
    }

    setStatus(message, focus = false) {
      this.status.textContent = message || '';
      if (this.busy && this.querySelector('[data-mobile-next]')) this.querySelector('[data-mobile-next]').textContent = message || this.messages.opening;
      if (focus) this.status.focus();
    }

    async json(url) {
      const response = await fetch(url, { credentials: 'omit', signal: AbortSignal.timeout(20000) });
      if (!response.ok) throw new Error('catalog');
      return response.json();
    }

    async load() {
      this.setStatus(this.messages.loading);
      this.querySelector('[data-retry]').hidden = true;
      this.checkout.disabled = true;
      this.serviceReady = false;
      // Il servizio può metterci qualche secondo a rispondere: finché non ha risposto il pulsante resta
      // spento, ma «non ancora acquistabile» si scrive solo se dice davvero di no.
      this.serviceChecked = false;
      this.updateMobileFlow();
      // Lo stato del servizio si chiede subito, insieme al catalogo: in fila le due attese si sommavano.
      let statoServizio = null;
      if (this.dataset.api) {
        try {
          const url = new URL(this.dataset.api, location.href);
          if (url.protocol === 'https:') {
            url.searchParams.set('action', 'status');
            statoServizio = this.json(url.href);
            statoServizio.catch(() => {});
          }
        } catch {}
      }
      try {
        const live = 'https://ilkuebbvrbkudpinzfsi.supabase.co/functions/v1/kibbu-catalogo';
        if (this.dataset.preview === 'true' && this.dataset.previewCatalog) {
          const [snapshot, current] = await Promise.all([this.json(this.dataset.previewCatalog), this.json(live).catch(() => null)]);
          if (!snapshot.preview_only) throw new Error('preview');
          const products = new Map(snapshot.products.map((product) => [String(product.id), product]));
          for (const product of current?.products || []) products.set(String(product.id), { ...product, status: 'ACTIVE' });
          this.catalog = { currency: current?.currency || snapshot.currency, products: [...products.values()] };
        } else this.catalog = await this.json(live);
        if (!this.isConnected) return;
        if (this.catalog.currency !== (this.dataset.currency || 'EUR')) throw new Error('currency');
        this.product = this.catalog.products.find((product) => product.kind === 'personalizzato' && (!this.dataset.productId || String(product.id) === this.dataset.productId));
        if (!this.product?.variants?.length) throw new Error('product');
        const variants = this.product.variants.filter((variant) => variant.available);
        if (!this.incomingApplied) {
          this.incomingApplied = true;
          const params = new URL(location.href).searchParams;
          const incoming = variants.find((variant) => String(variant.id) === params.get('variant'));
          if (incoming) { this.base = incoming.options.Base; this.color = incoming.options.Colorazione; }
          const bowl = this.catalog.products.find((product) => product.kind === 'ciotola' && product.variants.some((variant) => String(variant.id) === params.get('ciotola') && variant.available && variant.colorKey === incoming?.colorKey));
          if (bowl) { this.bowlId = String(bowl.id); this.bowlVariantId = params.get('ciotola'); this.bundle.checked = true; this.nameInput.value = (params.get('nome') || '').slice(0,12); }
        }
        if (!variants.length) throw new Error('variants');
        this.base ||= variants.some((variant) => variant.options.Base === 'Slim Lite') ? 'Slim Lite' : variants[0].options.Base;
        this.color ||= variants.some((variant) => variant.options.Base === this.base && variant.options.Colorazione === 'Sabbia del Deserto + Marrone Cioccolato') ? 'Sabbia del Deserto + Marrone Cioccolato' : variants[0].options.Colorazione;
        this.querySelectorAll('[data-config-fields]').forEach((fieldset) => { fieldset.disabled = false; });
        this.form.setAttribute('aria-busy', 'false');
        this.render();
        this.setStatus('');
        if (this.dataset.api && this.product.status !== 'DRAFT') {
          if (!statoServizio) throw new Error('api');
          const service = await statoServizio;
          if (!this.isConnected) return;
          this.serviceReady = service.ready === true;
          this.serviceChecked = true;
          this.updateAvailability();
        }
      } catch {
        this.setStatus(this.messages.error);
        this.querySelector('[data-retry]').hidden = false;
        this.checkout.disabled = true;
        this.form.setAttribute('aria-busy', 'false');
      }
    }

    resetRequest() {
      this.requestId = crypto.randomUUID();
      this.uploaded.clear();
    }

    changed() {
      this.resetRequest();
      this.render();
      this.setStatus('');
    }

    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
    }

    imageUrl(source, width = 540) {
      if (!source) return null;
      const url = new URL(source, location.href);
      if (url.protocol !== 'https:') return null;
      if (url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/')) url.searchParams.set('width', String(width));
      return url.href;
    }

    showImage(image, source, alt, width) {
      const url = this.imageUrl(source, width);
      image.hidden = !url;
      if (url) {
        image.removeAttribute('srcset');
        image.src = url;
        image.alt = alt;
      }
    }

    render() {
      const active = document.activeElement;
      const focusBase = this.contains(active) ? active?.dataset?.base : null;
      const focusColor = this.contains(active) ? active?.dataset?.color : null;
      const variants = this.product.variants.filter((variant) => variant.available);
      const bases = [...new Set(variants.map((variant) => variant.options.Base))];
      const order = ['Slim Lite', 'Slim', 'Clessidra'];
      bases.sort((a, b) => order.indexOf(a) - order.indexOf(b));
      if (!bases.includes(this.base)) this.base = bases[0];
      const choices = variants.filter((variant) => variant.options.Base === this.base);
      if (!choices.some((variant) => variant.options.Colorazione === this.color)) this.color = choices[0]?.options.Colorazione;
      this.primary = choices.find((variant) => variant.options.Colorazione === this.color);
      const baseContainer = this.querySelector('[data-bases]');
      baseContainer.replaceChildren();
      for (const base of bases) {
        const variant = variants.find((item) => item.options.Base === base && item.options.Colorazione === this.color) || variants.find((item) => item.options.Base === base);
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'kibbu-personalizzato__base'; button.dataset.base = base;
        button.setAttribute('aria-pressed', String(base === this.base));
        const image = document.createElement('img'); image.width = 160; image.height = 160; image.loading = 'lazy';
        this.showImage(image, variant.image || this.product.image, `${base} · ${variant.options.Colorazione}`, 240);
        const text = document.createElement('span'); text.textContent = base;
        button.append(image, text); baseContainer.append(button);
      }
      const colors = this.querySelector('[data-colors]'); colors.replaceChildren();
      for (const variant of choices) {
        const color = variant.options.Colorazione;
        const button = document.createElement('button'); button.type = 'button'; button.className = 'kibbu-personalizzato__color'; button.dataset.color = color;
        button.title = color; button.setAttribute('aria-label', color); button.setAttribute('aria-pressed', String(color === this.color));
        const swatch = document.createElement('span'); swatch.setAttribute('aria-hidden', 'true');
        const [first, second] = color.split(' + ');
        if (this.palette[first] && this.palette[second]) { swatch.style.setProperty('--kibbu-c1', this.palette[first]); swatch.style.setProperty('--kibbu-c2', this.palette[second]); }
        else { button.classList.add('kibbu-personalizzato__color--text'); swatch.textContent = color; }
        button.append(swatch); colors.append(button);
      }
      this.querySelector('[data-color-name]').textContent = this.color;
      this.querySelector('[data-selection]').textContent = `${this.base} · ${this.color}`;
      this.querySelector('[data-price]').textContent = this.money(this.primary.price);
      this.showImage(this.querySelector('[data-hero]'), this.primary.image || this.product.image, `${this.messages.example} ${this.base} · ${this.color}`, 900);
      this.renderBowl();
      const bowl = this.selectedBowlVariant;
      const total = this.bundle.checked && bowl ? Math.round(this.primary.price * .8) + Math.round(bowl.price * .8) : this.primary.price;
      this.querySelector('[data-total]').textContent = this.money(total);
      this.querySelector('[data-summary]').textContent = this.bundle.checked && bowl ? `${this.base} + ${this.selectedBowl.title.replace(/\s+portaciotola.*$/i, '')} · ${this.messages.total_bundle}` : `${this.base} · ${this.messages.total_single}`;
      this.updateAvailability();
      if (focusBase || focusColor) {
        const target = [...this.querySelectorAll('[data-base],[data-color]')].find(button => focusBase ? button.dataset.base === focusBase : button.dataset.color === focusColor);
        target?.focus({preventScroll:true});
      }
      if (this.mobileQuery?.matches) {
        const selected = colors.querySelector('[aria-pressed="true"]');
        if (selected) colors.scrollLeft += selected.getBoundingClientRect().left - colors.getBoundingClientRect().left - (colors.clientWidth - selected.offsetWidth) / 2;
      }
    }

    rememberSize() {
      const variant = this.selectedBowl?.variants.find((item) => String(item.id) === this.bowlVariantId);
      if (variant) this.bowlSize = Object.fromEntries(Object.entries(variant.options).filter(([key]) => key !== 'Colorazione'));
    }

    renderBowl() {
      const candidates = this.catalog.products.filter((product) => product.kind === 'ciotola' && product.variants.some((variant) => variant.available && variant.colorKey && variant.colorKey === this.primary.colorKey));
      candidates.sort((a, b) => Number(b.handle === 'rea-portaciotola-personalizzato') - Number(a.handle === 'rea-portaciotola-personalizzato'));
      const product = candidates.find((item) => String(item.id) === this.bowlId) || candidates[0];
      this.bowlSelect.replaceChildren();
      for (const item of candidates) this.bowlSelect.add(new Option(item.title.replace(/\s+portaciotola.*$/i, ''), item.id));
      this.selectedBowl = product;
      this.selectedBowlVariant = null;
      this.sizeSelect.replaceChildren();
      if (product) {
        this.bowlId = String(product.id); this.bowlSelect.value = this.bowlId;
        const variants = product.variants.filter((variant) => variant.available && variant.colorKey === this.primary.colorKey);
        for (const variant of variants) this.sizeSelect.add(new Option(Object.entries(variant.options).filter(([key]) => key !== 'Colorazione').map(([,value]) => value).join(' / '), variant.id));
        const selected = variants.find((variant) => String(variant.id) === this.bowlVariantId)
          || variants.find((variant) => Object.entries(variant.options).filter(([key]) => key !== 'Colorazione').every(([key, value]) => this.bowlSize?.[key] === value)) || variants[0];
        this.selectedBowlVariant = selected; this.bowlVariantId = String(selected.id); this.sizeSelect.value = this.bowlVariantId; this.rememberSize();
        this.showImage(this.querySelector('[data-bowl-image]'), selected.image || product.image, `${product.title} · ${selected.title}`, 360);
        const config = product.personalization;
        this.nameInput.required = this.bundle.checked && config?.enabled && config.required;
        this.nameInput.disabled = !this.bundle.checked || !config?.enabled;
        this.nameInput.maxLength = config?.maxLength || 12;
        this.querySelector('[data-name-label]').textContent = config?.label || this.messages.name_required;
        this.nameInput.hidden = !config?.enabled;
        this.querySelector('[data-name-label]').hidden = !config?.enabled;
      } else this.nameInput.disabled = true;
      this.querySelector('[data-bowl-fields]').hidden = !this.bundle.checked || !product;
      this.querySelector('[data-bowl-status]').textContent = this.bundle.checked && !product ? this.messages.bowl_unavailable : '';
      this.bowlSelect.disabled = !this.bundle.checked || !product;
      this.sizeSelect.disabled = !this.bundle.checked || !product;
    }

    updateAvailability() {
      const draft = this.product?.status === 'DRAFT';
      const unavailable = !this.primary?.available || (this.bundle.checked && !this.selectedBowlVariant);
      this.querySelector('[data-availability]').textContent = draft ? this.messages.preview : unavailable ? this.messages.unavailable : !this.serviceReady && this.serviceChecked ? this.messages.service_unavailable : '';
      this.checkout.disabled = this.busy || draft || unavailable || !this.serviceReady;
      this.updateMobileFlow();
    }

    updateMobileFlow() {
      const flow = this.querySelector('[data-mobile-flow]');
      if (!flow) return;
      flow.hidden = !this.primary || !this.serviceReady || this.product?.status === 'DRAFT';
      const button = this.querySelector('[data-mobile-next]');
      button.disabled = this.checkout.disabled;
      const ready = this.files.has('front') && this.emailInput.checkValidity() && (!this.nameInput.required || this.nameInput.checkValidity()) && this.querySelector('[data-consent]').checked;
      button.textContent = this.busy ? (this.status.textContent || this.messages.uploading) : !this.files.has('front') ? this.messages.mobile_add_photo : ready ? this.messages.mobile_pay : this.messages.mobile_complete;
      this.querySelector('[data-mobile-total]').textContent = this.querySelector('[data-total]').textContent;
      const profiles = Number(this.files.has('left')) + Number(this.files.has('right'));
      const count = this.querySelector('[data-profile-count]');
      if (count) count.textContent = profiles ? this.messages.mobile_profiles_count.replace('[count]',profiles) : '';
    }

    mobileNext() {
      if (this.busy || this.checkout.disabled) return;
      let target;
      if (!this.files.has('front')) {
        target = this.querySelector('[data-file="front"]');
        target.focus();
        target.click();
        return;
      }
      if (this.nameInput.required && !this.nameInput.checkValidity()) target = this.nameInput;
      else if (!this.emailInput.checkValidity()) target = this.emailInput;
      else if (!this.querySelector('[data-consent]').checked) target = this.querySelector('[data-consent]');
      if (target) {
        target.scrollIntoView({block:'center',behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto':'smooth'});
        target.focus({preventScroll:true});
      } else this.form.requestSubmit(this.checkout);
    }

    chooseFile(input) {
      const role = input.dataset.file;
      const file = input.files?.[0];
      if (!file) return;
      const extension = file.name.split('.').pop()?.toLowerCase();
      const allowed = ['jpg','jpeg','png','webp','heic','heif'];
      if (!allowed.includes(extension) || (file.type && !['image/jpeg','image/png','image/webp','image/heic','image/heif'].includes(file.type))) { this.removeFile(role); this.setStatus(this.messages.photo_type_error, true); return; }
      if (file.size <= 0 || file.size > 20 * 1024 * 1024) { this.removeFile(role); this.setStatus(this.messages.photo_size_error, true); return; }
      if (this.objectUrls.has(role)) URL.revokeObjectURL(this.objectUrls.get(role));
      this.files.set(role, file);
      const url = URL.createObjectURL(file); this.objectUrls.set(role, url);
      const card = this.querySelector(`[data-photo="${role}"]`);
      const image = card.querySelector('[data-photo-image]'); image.src = url; image.hidden = false;
      image.onerror = () => { image.hidden = true; card.querySelector('[data-photo-icon]').textContent = '✓'; card.querySelector('[data-photo-icon]').hidden = false; };
      card.querySelector('[data-photo-icon]').hidden = true;
      card.querySelector('[data-photo-label]').textContent = this.messages.replace_photo;
      card.querySelector('[data-filename]').textContent = file.name;
      card.querySelector('[data-remove]').hidden = false;
      this.resetRequest(); this.setStatus(''); this.updateMobileFlow();
    }

    removeFile(role) {
      const input = this.querySelector(`[data-file="${role}"]`); input.value = '';
      this.files.delete(role);
      if (this.objectUrls.has(role)) URL.revokeObjectURL(this.objectUrls.get(role));
      this.objectUrls.delete(role);
      const card = this.querySelector(`[data-photo="${role}"]`);
      card.querySelector('[data-photo-image]').hidden = true;
      card.querySelector('[data-photo-image]').removeAttribute('src');
      card.querySelector('[data-photo-icon]').textContent = '+';
      card.querySelector('[data-photo-icon]').hidden = false;
      card.querySelector('[data-photo-label]').textContent = this.messages.choose_photo;
      card.querySelector('[data-filename]').textContent = '';
      card.querySelector('[data-remove]').hidden = true;
      this.resetRequest(); this.setStatus(''); this.updateMobileFlow();
    }

    async api(body) {
      const response = await fetch(this.dataset.api, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: AbortSignal.timeout(65000) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || this.messages.checkout_error);
      return data;
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.serviceReady || this.product?.status === 'DRAFT' || !this.primary?.available || (this.bundle.checked && !this.selectedBowlVariant)) return;
      if (!this.files.has('front')) { this.setStatus(this.messages.photo_required, true); return; }
      if (!this.querySelector('[data-consent]').checked) { this.setStatus(this.messages.consent_required, true); return; }
      this.nameInput.value = this.nameInput.value.trim();
      if (!this.form.reportValidity()) return;
      this.busy = true;
      this.querySelectorAll('button,input,select,fieldset').forEach((element) => { element.dataset.wasDisabled = String(element.disabled); element.disabled = true; });
      this.form.setAttribute('aria-busy', 'true');
      this.updateAvailability();
      try {
        const entries = ['front','left','right'].filter((role) => this.files.has(role)).map((role) => ({ role, file: this.files.get(role) }));
        const missing = entries.filter(({ role }) => !this.uploaded.has(role));
        if (missing.length) {
          this.setStatus(this.messages.uploading);
          const prepared = await this.api({ action: 'upload', consent: true, request_id: this.requestId, files: missing.map(({role,file}) => ({ role, name: file.name, type: file.type, size: file.size })) });
          for (const { role, file } of missing) {
            const upload = prepared.files?.find((item) => item.role === role);
            if (upload?.already_uploaded && upload.path) { this.uploaded.set(role, upload.path); continue; }
            if (!upload?.url || !upload.path || upload.method !== 'PUT') throw new Error(this.messages.upload_error);
            const url = new URL(upload.url);
            if (url.protocol !== 'https:' || !url.pathname.startsWith('/storage/v1/object/upload/sign/foto-cani/')) throw new Error(this.messages.upload_error);
            const response = await fetch(url.href, { method: 'PUT', credentials: 'omit', headers: { 'Content-Type': file.type || 'application/octet-stream', 'x-upsert': 'false' }, body: file, signal: AbortSignal.timeout(120000) });
            if (!response.ok) throw new Error(this.messages.upload_error);
            this.uploaded.set(role, upload.path);
          }
        }
        this.setStatus(this.messages.opening);
        const result = await this.api({ action: 'checkout', customer_email: this.emailInput.value.trim(), request_id: this.requestId, primary_variant_id: String(this.primary.id), companion_variant_id: this.bundle.checked ? String(this.selectedBowlVariant.id) : undefined, companion_name: this.bundle.checked && !this.nameInput.hidden ? this.nameInput.value : undefined, photos: entries.map(({role}) => ({ role, path: this.uploaded.get(role) })), consent: true });
        const url = new URL(result.url);
        const hosts = (this.dataset.checkoutHosts || '').split(',').filter(Boolean);
        if (url.protocol !== 'https:' || !hosts.includes(url.hostname)) throw new Error(this.messages.checkout_error);
        location.assign(url.href);
      } catch (error) {
        this.setStatus(error.message || this.messages.checkout_error, true);
        this.busy = false;
        this.querySelectorAll('[data-was-disabled]').forEach((element) => { element.disabled = element.dataset.wasDisabled === 'true'; delete element.dataset.wasDisabled; });
        this.form.setAttribute('aria-busy', 'false'); this.updateAvailability();
      }
    }
  });
}
